import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, Copy, CheckCircle, X, AlertCircle } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  if (trialUser) return 'trial_user_token';
  return null;
};

const EmailResponseAssistant = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Email Response Assistant - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [originalEmail, setOriginalEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [context, setContext] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [generating, setGenerating] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!originalEmail.trim()) {
      toast.error('Please paste the email content');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/email-response/generate`,
        {
          original_email: originalEmail,
          sender_name: senderName || null,
          sender_email: senderEmail || null,
          subject: subject || null,
          context: context || null,
          urgency: urgency
        },
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setGeneratedResponse(response.data);
      toast.success('Response generated successfully!');
    } catch (error) {
      console.error('Error generating response:', error);
      toast.error(error.response?.data?.detail || 'Error generating response');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyResponse = () => {
    if (generatedResponse) {
      navigator.clipboard.writeText(generatedResponse.generated_response);
      setCopied(true);
      toast.success('Response copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setOriginalEmail('');
    setSenderName('');
    setSenderEmail('');
    setSubject('');
    setContext('');
    setUrgency('normal');
    setGeneratedResponse(null);
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Mail size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Email Response Assistant" />
            <p className="tool-subtitle">AI-powered professional email responses for lawyers</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!generatedResponse ? (
          <>
            {/* Input Form */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Generate Email Response</CardTitle>
                <CardDescription>
                  Paste the email content below and Lawra will generate a professional response
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Original Email Content *</label>
                  <textarea
                    value={originalEmail}
                    onChange={(e) => setOriginalEmail(e.target.value)}
                    placeholder="Paste the email you received here..."
                    className="form-textarea"
                    rows={8}
                    style={{ width: '100%', minHeight: '200px' }}
                  />
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Sender Name</label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="John Smith"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Sender Email</label>
                    <input
                      type="email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      placeholder="john.smith@example.com"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Email Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Re: Legal Consultation Request"
                    className="form-input"
                  />
                </div>

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Additional Context</label>
                  <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Any additional context about this case or client..."
                    className="form-textarea"
                    rows={3}
                  />
                </div>

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label className="form-label" style={{ color: '#253B48', fontWeight: '600' }}>Urgency Level</label>
                  <select
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    className="form-select"
                  >
                    <option value="low">Low - Routine inquiry</option>
                    <option value="normal">Normal - Standard matter</option>
                    <option value="high">High - Urgent response needed</option>
                  </select>
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                onClick={handleGenerate}
                disabled={generating || !originalEmail.trim()}
                loading={generating}
                size="lg"
              >
                Generate Response
              </AnimatedActionButton>
                  <LedProgressLine isActive={generating} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Generated Response */}
            <div className="results-header">
              <h2>Generated Response</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button onClick={handleCopyResponse} variant="outline">
                  {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                  {copied ? 'Copied!' : 'Copy Response'}
                </Button>
                <Button onClick={handleReset} variant="outline">
                  <Mail size={18} />
                  New Response
                </Button>
              </div>
            </div>

            <Card className="result-card">
              <CardHeader>
                <CardTitle>Your Email Response</CardTitle>
                <CardDescription>
                  Review and copy this response to use in your email client
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="email-response-box">
                  <div className="response-content">
                    {generatedResponse.generated_response.split('\n').map((paragraph, idx) => (
                      <p key={idx} style={{ marginBottom: paragraph.trim() ? '1rem' : '0.5rem' }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(218, 165, 32, 0.1)', borderRadius: '8px', border: '1px solid rgba(218, 165, 32, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <AlertCircle size={20} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--gold)' }}>Important:</strong> This is an AI-generated draft response. 
                      Please review carefully and modify as needed before sending. Add appropriate salutation, 
                      closing, and signature according to your preferences.
                    </div>
                  </div>
        <ReportIssueBar toolName="Email Response Assistant" />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailResponseAssistant;
